import { useEffect } from 'react';

const Gallery = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    {
      name: 'Weddings',
      color: 'from-[#fa709a] to-[#fee140]',
      events: [
        { 
          title: 'Garden Wedding', 
          guests: '150 guests', 
          location: 'Outdoor Venue',
          image: 'https://images.unsplash.com/photo-1723832348105-2e69f948135a?w=600&q=80'
        },
        { 
          title: 'Beach Ceremony', 
          guests: '100 guests', 
          location: 'Beachfront Resort',
          image: 'https://images.unsplash.com/photo-1647296020388-787fdae78e3c?w=600&q=80'
        },
        { 
          title: 'Ballroom Reception', 
          guests: '250 guests', 
          location: 'Grand Hotel',
          image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&q=80'
        },
        { 
          title: 'Rustic Barn Wedding', 
          guests: '120 guests', 
          location: 'Country Estate',
          image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&q=80'
        }
      ]
    },
    {
      name: 'Corporate Events',
      color: 'from-[#4facfe] to-[#00f2fe]',
      events: [
        { 
          title: 'Annual Gala', 
          guests: '500 guests', 
          location: 'Convention Center',
          image: 'https://images.pexels.com/photos/50675/banquet-wedding-society-deco-50675.jpeg?w=600&q=80'
        },
        { 
          title: 'Product Launch', 
          guests: '200 guests', 
          location: 'Downtown Venue',
          image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80'
        },
        { 
          title: 'Team Building', 
          guests: '75 guests', 
          location: 'Resort',
          image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&q=80'
        },
        { 
          title: 'Holiday Party', 
          guests: '300 guests', 
          location: 'Corporate Office',
          image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80'
        }
      ]
    },
    {
      name: 'Private Celebrations',
      color: 'from-[#a8edea] to-[#fed6e3]',
      events: [
        { 
          title: 'Milestone Birthday', 
          guests: '80 guests', 
          location: 'Private Home',
          image: 'https://images.unsplash.com/photo-1631857455684-a54a2f03665f?w=600&q=80'
        },
        { 
          title: 'Anniversary Party', 
          guests: '60 guests', 
          location: 'Restaurant',
          image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&q=80'
        },
        { 
          title: 'Graduation', 
          guests: '100 guests', 
          location: 'Backyard',
          image: 'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=600&q=80'
        },
        { 
          title: 'Baby Shower', 
          guests: '40 guests', 
          location: 'Garden Venue',
          image: 'https://images.unsplash.com/photo-1544155892-b2b6c64204fc?w=600&q=80'
        }
      ]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden" 
        data-testid="gallery-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(250, 112, 154, 0.85), rgba(254, 225, 64, 0.85)), url(https://images.unsplash.com/photo-1633424414664-c24a6d28086b?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-content fade-in-up">
          <h1 className="hero-title text-white drop-shadow-lg" data-testid="gallery-title">Event Gallery</h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md" data-testid="gallery-subtitle">
            A showcase of memorable events we've had the pleasure to create
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section" style={{ background: 'white' }} data-testid="gallery-content-section">
        {categories.map((category, catIndex) => (
          <div key={catIndex} className="mb-16" data-testid={`gallery-category-${catIndex}`}>
            <h2 className="text-3xl font-bold text-[#8b4513] mb-2 text-center">{category.name}</h2>
            <div className="decorative-line"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {category.events.map((event, eventIndex) => (
                <div
                  key={eventIndex}
                  className="card group cursor-pointer overflow-hidden"
                  data-testid={`gallery-item-${catIndex}-${eventIndex}`}
                >
                  <div className="aspect-[4/3] overflow-hidden rounded-lg mb-4 relative">
                    <img 
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-[#8b4513]">{event.title}</h3>
                  <p className="text-sm text-[#6b4423] mb-1">{event.guests}</p>
                  <p className="text-sm text-[#d2691e]">{event.location}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Testimonials Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="gallery-testimonials-section">
        <h2 className="section-title text-white">What Our Clients Say</h2>
        <div className="decorative-line bg-white"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          <div className="card" data-testid="gallery-testimonial-0">
            <p className="text-[#6b4423] italic mb-4">
              "JFRAI made our wedding day absolutely perfect. The food was incredible and the service
              was impeccable. Our guests are still talking about it!"
            </p>
            <p className="text-[#8b4513] font-bold">- Sarah & Michael</p>
            <p className="text-sm text-[#d2691e]">Wedding Reception</p>
          </div>
          <div className="card" data-testid="gallery-testimonial-1">
            <p className="text-[#6b4423] italic mb-4">
              "We've used JFRAI for multiple corporate events. They're professional, reliable, and the
              food is always outstanding. Highly recommend!"
            </p>
            <p className="text-[#8b4513] font-bold">- TechCorp Inc.</p>
            <p className="text-sm text-[#d2691e]">Corporate Catering</p>
          </div>
          <div className="card" data-testid="gallery-testimonial-2">
            <p className="text-[#6b4423] italic mb-4">
              "The Caribbean dishes were authentic and delicious! JFRAI brought the taste of the islands
              to our celebration. Everyone loved it!"
            </p>
            <p className="text-[#8b4513] font-bold">- Jennifer L.</p>
            <p className="text-sm text-[#d2691e]">Anniversary Party</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;